import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTemplateEditorHistory } from "../editor/useTemplateEditorHistory";
import {
  applyOverlayBox,
  applyOverlayBoxes,
  copyNodesByIds,
  deleteNodesByIds,
  findNodePath,
  getAbsolutePx,
  insertNodeAtOverlay,
  isCanvasBackgroundNode,
  listNodeNames,
  pasteCopiedNodes,
  patchNodeById,
  resolveInsertParentID,
  setNodeVisible,
} from "../utils/layerTree";
import {
  CANVAS_CLIPBOARD_KIND,
  consumeClipboardPasteCount,
  getCanvasClipboard,
  setCanvasClipboard,
} from "../utils/canvasClipboard";
import {
  addInstanceAsset,
  applySiblingDisplayOrder,
  cloneInstance,
  createInstanceNode,
  flattenInstanceItems,
} from "./instanceGraph";
import {
  nextLayerSelection,
  nextMarqueeSelection,
  resolveOperationIds,
} from "../editor/canvasLayerSelection";
import {
  fitImageOverlayBox,
  sizeOverlayBoxToNaturalImage,
} from "../utils/fitImageOverlayBox";
import {
  collectTextFontAssetRefsFromNodes,
  defaultTextStyleFromCatalog,
} from "../utils/defaultTextFont";
import {
  getImageDimensionsFromUrl,
  readImageDimensionsFromUrl,
} from "../utils/getImageDimensionsFromFile";
import { listStoredInlineReplacements } from "../utils/textInlineReplacements";

const hydrate = (instance) => ({
  artboardID: instance?.artboardID || "primary",
  width: Math.round(Number(instance?.width) || 0),
  height: Math.round(Number(instance?.height) || 0),
  nodes: instance?.nodes || {},
  nodeLayouts: instance?.nodeLayouts || {},
  assets: instance?.assets || {},
});

const useTemplateInstance = ({
  instance,
  enabled,
  createID,
  fontFamiliesRef,
  brandFontFamilyRef,
}) => {
  const [artboardID, setArtboardID] = useState("primary");
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [nodes, setNodes] = useState({});
  const [nodeLayouts, setNodeLayouts] = useState({});
  const [assets, setAssets] = useState({});
  const [selectedID, setSelectedIDState] = useState(null);
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [editingTextID, setEditingTextID] = useState(null);
  const [expandedIDs, setExpandedIDs] = useState({});
  const [layersDirty, setLayersDirty] = useState(false);
  const selectedIDRef = useRef(null);
  const selectedIDsRef = useRef([]);
  const createIDRef = useRef(createID);
  const graphRef = useRef({
    nodes: {},
    nodeLayouts: {},
    assets: {},
  });

  selectedIDRef.current = selectedID;
  selectedIDsRef.current = selectedIDs;
  createIDRef.current = createID;
  graphRef.current = { nodes, nodeLayouts, assets };

  const applySelection = useCallback((id, ids) => {
    const nextIDs = Array.isArray(ids) ? ids.filter(Boolean) : id ? [id] : [];
    const primary = id ?? nextIDs[nextIDs.length - 1] ?? null;
    selectedIDRef.current = primary;
    selectedIDsRef.current = nextIDs;
    setSelectedIDState(primary);
    setSelectedIDs(nextIDs);
  }, []);

  const setSelectedID = useCallback(
    (idOrUpdater) => {
      const next =
        typeof idOrUpdater === "function"
          ? idOrUpdater(selectedIDRef.current)
          : idOrUpdater;
      applySelection(next, next ? [next] : []);
    },
    [applySelection]
  );

  const selectLayer = useCallback(
    (id, event, options = {}) => {
      const next = nextLayerSelection({
        currentIDs: selectedIDsRef.current,
        id,
        event,
        preserveMulti: options.preserveMulti,
      });
      applySelection(next.primary, next.ids);
    },
    [applySelection]
  );

  const selectLayers = useCallback(
    (ids, event) => {
      const next = nextMarqueeSelection({
        currentIDs: selectedIDsRef.current,
        ids,
        event,
      });
      applySelection(next.primary, next.ids);
    },
    [applySelection]
  );

  const getSnapshot = useCallback(
    () => ({
      artboardID,
      width,
      height,
      nodes,
      nodeLayouts,
      assets,
      selectedID,
      selectedIDs,
      expandedIDs,
    }),
    [
      artboardID,
      assets,
      expandedIDs,
      height,
      nodeLayouts,
      nodes,
      selectedID,
      selectedIDs,
      width,
    ]
  );

  const applySnapshot = useCallback(
    (snapshot) => {
      setArtboardID(snapshot.artboardID);
      setWidth(snapshot.width);
      setHeight(snapshot.height);
      setNodes(snapshot.nodes || {});
      setNodeLayouts(snapshot.nodeLayouts || {});
      setAssets(snapshot.assets || {});
      applySelection(
        snapshot.selectedID || null,
        snapshot.selectedIDs ||
          (snapshot.selectedID ? [snapshot.selectedID] : [])
      );
      setExpandedIDs(snapshot.expandedIDs || {});
      setEditingTextID(null);
      setLayersDirty(true);
    },
    [applySelection]
  );

  const { push, endCoalesce, undo, redo, clear } = useTemplateEditorHistory({
    getSnapshot,
    applySnapshot,
  });

  useEffect(() => {
    if (!enabled) return;
    const next = hydrate(cloneInstance(instance));
    setArtboardID(next.artboardID);
    setWidth(next.width);
    setHeight(next.height);
    setNodes(next.nodes);
    setNodeLayouts(next.nodeLayouts);
    setAssets(next.assets);
    setSelectedID(null);
    setEditingTextID(null);
    setExpandedIDs({});
    setLayersDirty(false);
    clear();
    // eslint-disable-next-line
  }, [clear, enabled, instance]);

  const commitGraph = useCallback(
    ({ nextNodes = nodes, nextLayouts = nodeLayouts, nextAssets = assets }) => {
      setNodes(nextNodes);
      setNodeLayouts(nextLayouts);
      setAssets(nextAssets);
      setLayersDirty(true);
    },
    [assets, nodeLayouts, nodes]
  );

  const items = useMemo(
    () =>
      flattenInstanceItems({
        nodes,
        nodeLayouts,
        assets,
      }),
    [assets, nodeLayouts, nodes]
  );

  const selectedItem = items.find((item) => item.id === selectedID) || null;

  const updateBox = useCallback(
    ({ id, nx, ny, nw, nh, rotation, crop }) => {
      const found = findNodePath({ nodes: graphRef.current.nodes, nodeID: id });
      if (found?.node?.isBackground && !found.parentID) {
        return;
      }
      push({ coalesceKey: `transform:${id}` });
      const result = applyOverlayBox({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeID: id,
        nx,
        ny,
        nw,
        nh,
        rotation,
        crop,
        artboardWidth: width,
        artboardHeight: height,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
    },
    [commitGraph, height, push, width]
  );

  const updateBoxes = useCallback(
    (updates = []) => {
      if (!updates.length) return;
      const ids = updates.map((item) => item.id).sort();
      push({ coalesceKey: `transform:${ids.join(",")}` });
      const result = applyOverlayBoxes({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        updates,
        artboardWidth: width,
        artboardHeight: height,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
    },
    [commitGraph, height, push, width]
  );

  const patchSelected = useCallback(
    ({ patch, assetPatch, nodeID }) => {
      const id = nodeID || selectedIDRef.current;
      if (!id) return;
      push();
      const nextNodes = patch
        ? patchNodeById({
            nodes: graphRef.current.nodes,
            nodeID: id,
            patch,
          })
        : graphRef.current.nodes;
      commitGraph({
        nextNodes,
        nextAssets: assetPatch
          ? { ...graphRef.current.assets, ...assetPatch }
          : graphRef.current.assets,
      });
    },
    [commitGraph, push]
  );

  const renameLayer = useCallback(
    (nodeID, name) => {
      const trimmed = String(name || "").trim();
      if (!trimmed) return;
      push();
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID,
          patch: { name: trimmed },
        }),
      });
    },
    [commitGraph, push]
  );

  const setTextValue = useCallback(
    (nodeID, value) => {
      const found = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID,
      });
      if (found?.node?.type !== "text") return;
      push({ coalesceKey: `text:${nodeID}` });
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID,
          patch: {
            source: {
              kind: "fixed",
              value: String(value || ""),
            },
          },
        }),
      });
    },
    [commitGraph, push]
  );

  const addLayer = useCallback(
    ({ type, url, name, id }) => {
      if (!id) return null;
      if (type !== "text" && type !== "image") return null;
      const parentID = resolveInsertParentID({
        nodes: graphRef.current.nodes,
        selectedID: selectedIDRef.current,
      });
      let nextAssets = graphRef.current.assets;
      const node = createInstanceNode({
        id,
        name,
        existingNames: listNodeNames({ nodes: graphRef.current.nodes }),
        type,
      });
      if (type === "text") {
        const defaultStyle = defaultTextStyleFromCatalog({
          families: fontFamiliesRef?.current || [],
          brandFontFamily: brandFontFamilyRef?.current || "",
          existingFontAssetRefs: collectTextFontAssetRefsFromNodes({
            nodes: graphRef.current.nodes,
          }),
        });
        if (defaultStyle.fontAssetRef) {
          node.style = { ...(node.style || {}), ...defaultStyle };
        }
      }
      if (type === "image" && url) {
        const added = addInstanceAsset({
          assets: nextAssets,
          name: node.name,
          url,
        });
        nextAssets = added.assets;
        node.source = { kind: "asset", assetRef: added.assetRef };
      }
      const result = insertNodeAtOverlay({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        parentID,
        node,
        nx: 0.12,
        ny: 0.12,
        nw: type === "text" ? 0.4 : 0.28,
        nh: type === "text" ? 0.12 : 0.28,
        artboardWidth: width,
        artboardHeight: height,
      });
      if (!result.ok) return null;
      push();
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
        nextAssets,
      });
      setSelectedID(id);
      if (parentID) {
        setExpandedIDs((current) => ({ ...current, [parentID]: true }));
      }
      return id;
    },
    // eslint-disable-next-line
    [commitGraph, height, push, width]
  );

  const addImageLayers = useCallback(
    async ({ images = [], name }) => {
      const validImages = (Array.isArray(images) ? images : []).filter(
        (image) => image?.id && image?.url
      );
      if (!validImages.length) return [];

      const prepared = [];
      for (const image of validImages) {
        const dimensions = await readImageDimensionsFromUrl({ url: image.url });
        prepared.push({ ...image, ...dimensions });
      }

      const parentID = resolveInsertParentID({
        nodes: graphRef.current.nodes,
        selectedID: selectedIDRef.current,
      });
      let nextNodes = graphRef.current.nodes;
      let nextLayouts = graphRef.current.nodeLayouts;
      let nextAssets = graphRef.current.assets;
      const existingNames = listNodeNames({ nodes: nextNodes });
      const insertedIDs = [];

      prepared.forEach((image) => {
        const node = createInstanceNode({
          id: image.id,
          name,
          existingNames,
          type: "image",
        });
        existingNames.push(node.name);
        const added = addInstanceAsset({
          assets: nextAssets,
          name: node.name,
          url: image.url,
        });
        nextAssets = added.assets;
        node.source = { kind: "asset", assetRef: added.assetRef };
        const offset = 0.04 * insertedIDs.length;
        const { nw, nh } = fitImageOverlayBox({
          imageWidth: image.width,
          imageHeight: image.height,
          canvasWidth: width,
          canvasHeight: height,
        });
        const result = insertNodeAtOverlay({
          nodes: nextNodes,
          nodeLayouts: nextLayouts,
          parentID,
          node,
          nx: 0.12 + offset,
          ny: 0.12 + offset,
          nw,
          nh,
          artboardWidth: width,
          artboardHeight: height,
        });
        if (!result.ok) return;
        nextNodes = result.nodes;
        nextLayouts = result.nodeLayouts;
        insertedIDs.push(image.id);
      });

      if (!insertedIDs.length) return [];
      push();
      commitGraph({
        nextNodes,
        nextLayouts,
        nextAssets,
      });
      const lastID = insertedIDs[insertedIDs.length - 1];
      setSelectedID(lastID);
      if (parentID) {
        setExpandedIDs((current) => ({ ...current, [parentID]: true }));
      }
      return insertedIDs;
    },
    // eslint-disable-next-line
    [commitGraph, height, push, width]
  );

  const deleteLayer = useCallback(
    (nodeID) => {
      const { ids: requested, expandsSelection } = resolveOperationIds({
        explicitID: nodeID,
        selectedIDs: selectedIDsRef.current,
        fallbackID: selectedIDRef.current,
      });
      const ids = requested.filter((id) => {
        const found = findNodePath({
          nodes: graphRef.current.nodes,
          nodeID: id,
        });
        if (!found) return false;
        // An explicit single id may delete a background; hotkeys/multi may not.
        return (
          (!expandsSelection && nodeID) ||
          !isCanvasBackgroundNode({
            node: found.node,
            parentID: found.parentID,
          })
        );
      });
      if (ids.length === 0) return false;
      push();
      const result = deleteNodesByIds({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeIDs: ids,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
      const remainingIDs = selectedIDsRef.current.filter((item) =>
        findNodePath({ nodes: result.nodes, nodeID: item })
      );
      if (remainingIDs.length !== selectedIDsRef.current.length) {
        applySelection(
          remainingIDs.includes(selectedIDRef.current)
            ? selectedIDRef.current
            : remainingIDs[remainingIDs.length - 1] || null,
          remainingIDs
        );
      }
      setEditingTextID((current) =>
        current && !findNodePath({ nodes: result.nodes, nodeID: current })
          ? null
          : current
      );
      return true;
    },
    [applySelection, commitGraph, push]
  );

  const copyLayer = useCallback(
    (nodeID) => {
      const { ids: nodeIDs } = resolveOperationIds({
        explicitID: nodeID,
        selectedIDs: selectedIDsRef.current,
        fallbackID: selectedIDRef.current,
      });
      const payload = copyNodesByIds({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        assets: graphRef.current.assets,
        nodeIDs,
        artboardWidth: width,
        artboardHeight: height,
      });
      if (!payload) return false;
      setCanvasClipboard(payload);
      return true;
    },
    [width, height]
  );

  const pasteLayer = useCallback(() => {
    const payload = getCanvasClipboard();
    if (payload?.kind !== CANVAS_CLIPBOARD_KIND.TEMPLATE_NODE) return false;
    const createNextID = createIDRef.current;
    if (typeof createNextID !== "function") return false;
    const pasteCount = consumeClipboardPasteCount();
    const result = pasteCopiedNodes({
      nodes: graphRef.current.nodes,
      nodeLayouts: graphRef.current.nodeLayouts,
      assets: graphRef.current.assets,
      payload,
      selectedID: selectedIDRef.current,
      createID: createNextID,
      artboardWidth: width,
      artboardHeight: height,
      pasteCount,
    });
    if (!result.ok) return false;
    push();
    commitGraph({
      nextNodes: result.nodes,
      nextLayouts: result.nodeLayouts,
      nextAssets: result.assets,
    });
    if (result.pastedID) {
      applySelection(result.pastedID, result.pastedIDs);
      const parentID = findNodePath({
        nodes: result.nodes,
        nodeID: result.pastedID,
      })?.parentID;
      if (parentID) {
        setExpandedIDs((current) => ({ ...current, [parentID]: true }));
      }
    }
    return true;
    // eslint-disable-next-line
  }, [commitGraph, height, push, width]);

  const reorderSiblings = useCallback(
    ({ parentID, orderedDisplayIds }) => {
      push();
      const result = applySiblingDisplayOrder({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        parentID,
        orderedDisplayIds,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
    },
    [commitGraph, push]
  );

  const toggleLock = useCallback(
    (id, locked) => {
      const found = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID: id,
      });
      if (!found) return;
      push();
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID: id,
          patch: { locked: Boolean(locked) },
        }),
      });
    },
    [commitGraph, push]
  );

  const toggleVisible = useCallback(
    (id, visible) => {
      const found = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID: id,
      });
      if (!found) return;
      push();
      const result = setNodeVisible({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeID: id,
        visible: Boolean(visible),
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
    },
    [commitGraph, push]
  );

  const replaceImage = useCallback(
    async ({ nodeID, url, name }) => {
      if (!url) return;
      const found = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID,
      });
      if (!found) return;

      let overlay = null;
      try {
        const dimensions = await getImageDimensionsFromUrl({ url });
        if (dimensions.width > 0 && dimensions.height > 0 && width && height) {
          const currentBox = getAbsolutePx({
            nodes: graphRef.current.nodes,
            nodeLayouts: graphRef.current.nodeLayouts,
            nodeID,
            artboardWidth: width,
            artboardHeight: height,
          }) || { x: 0, y: 0, width: 0, height: 0 };
          overlay = sizeOverlayBoxToNaturalImage({
            imageWidth: dimensions.width,
            imageHeight: dimensions.height,
            canvasWidth: width,
            canvasHeight: height,
            currentNx: currentBox.x / width,
            currentNy: currentBox.y / height,
            currentNw: currentBox.width / width,
            currentNh: currentBox.height / height,
          });
        }
      } catch {
        // Keep the previous box if the image cannot be measured.
      }

      push();
      const added = addInstanceAsset({
        assets: graphRef.current.assets,
        name: name || nodeID,
        url,
      });
      let nextNodes = patchNodeById({
        nodes: graphRef.current.nodes,
        nodeID,
        patch: {
          source: { kind: "asset", assetRef: added.assetRef },
        },
      });
      let nextLayouts = graphRef.current.nodeLayouts;
      if (overlay) {
        const sized = applyOverlayBox({
          nodes: nextNodes,
          nodeLayouts: nextLayouts,
          nodeID,
          nx: overlay.nx,
          ny: overlay.ny,
          nw: overlay.nw,
          nh: overlay.nh,
          crop: null,
          artboardWidth: width,
          artboardHeight: height,
        });
        nextNodes = sized.nodes;
        nextLayouts = sized.nodeLayouts;
      }
      commitGraph({
        nextNodes,
        nextLayouts,
        nextAssets: added.assets,
      });
    },
    [commitGraph, height, push, width]
  );

  const assignInlineReplacementAsset = useCallback(
    ({ nodeID, slotID, url, name }) => {
      if (!url || !slotID) return;
      const found = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID,
      });
      if (found?.node?.type !== "text") return;
      const slots = listStoredInlineReplacements(
        found.node.style?.inlineReplacements
      );
      const slot = slots.find((item) => item.id === slotID);
      if (!slot) return;
      push();
      const added = addInstanceAsset({
        assets: graphRef.current.assets,
        name: name || slot.phrase || nodeID,
        url,
      });
      const nextSlots = slots.map((item) =>
        item.id === slotID ? { ...item, assetRef: added.assetRef } : item
      );
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID,
          patch: {
            style: {
              ...(found.node.style || {}),
              inlineReplacements: nextSlots,
            },
          },
        }),
        nextAssets: added.assets,
      });
    },
    [commitGraph, push]
  );

  const getPersistInstance = useCallback(
    () => ({
      artboardID,
      width,
      height,
      nodes,
      nodeLayouts,
      assets,
    }),
    [artboardID, assets, height, nodeLayouts, nodes, width]
  );

  return {
    artboardID,
    width,
    height,
    nodes,
    nodeLayouts,
    assets,
    items,
    selectedID,
    selectedIDs,
    selectedItem,
    editingTextID,
    expandedIDs,
    layersDirty,
    setSelectedID,
    selectLayer,
    selectLayers,
    setEditingTextID,
    setExpandedIDs,
    updateBox,
    updateBoxes,
    patchSelected,
    renameLayer,
    setTextValue,
    addLayer,
    addImageLayers,
    deleteLayer,
    copyLayer,
    pasteLayer,
    reorderSiblings,
    toggleLock,
    toggleVisible,
    replaceImage,
    assignInlineReplacementAsset,
    undo,
    redo,
    endCoalesce,
    getPersistInstance,
  };
};

export default useTemplateInstance;
